/**
 * BoardEntryCardV2 — Empty-board welcome overlay (Figma node 11770:252839).
 *
 * Shows centered card with:
 *  - "How can I help you today?" heading
 *  - AI engineer avatar selection (Plant Support, Chemical, Mechanical)
 *  - Chat input with agent selector, mic, and send button
 *
 * The "Call AI Engineer" action lives in the persistent BoardTopBarV2 (top-right);
 * it was removed from this card to avoid a duplicate button on the empty board
 * (FS-514).
 *
 * States: empty (placeholder) -> filled (user typed) -> submitted (hidden)
 */

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import {
  ChevronDown,
  ArrowRight,
  Loader2,
} from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'
import { getDisplayName } from '@/constants/agentNames'
import { useAppStore } from '@/store'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import {
  AgentMentionAutocomplete,
  AGENT_REGISTRY,
  type AgentOption,
} from '@/components/ChatInterface/AgentMentionAutocomplete'
import ChatVoice from '@/components/ChatInterface/ChatVoice'
import { CatalogScopeToggle } from '@/components/ChatInterface/CatalogScopeToggle'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'

// Fuzzy match used by the autocomplete for keyboard navigation parity
const fuzzyMatch = (query: string, text: string): boolean => {
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  if (t.includes(q)) return true
  let textIndex = 0
  for (let i = 0; i < q.length; i++) {
    const charIndex = t.indexOf(q[i], textIndex)
    if (charIndex === -1) return false
    textIndex = charIndex + 1
  }
  return true
}

// Map store agent IDs to background colors for avatar display
const AGENT_BG_COLORS: Record<string, string> = {
  'plant-support': '#FEF08A',
  'chemical-agent': '#BFDBFE',
  'mechanical-agent': '#BBF7D0',
  'electrical-agent': '#E9D5FF',
  'maint-agent': '#DDD6FE',
  'epc-agent': '#FED7AA',
}

const DEFAULT_BG_COLOR = '#E5E7EB'

// Map store agent IDs to canonical chat agent IDs used by setActiveAgent
const STORE_TO_CANONICAL: Record<string, string> = {
  'plant-support': 'plant_support_engineer',
  'chemical-agent': 'chemical_engineer',
  'mechanical-agent': 'mechanical_engineer',
  'electrical-agent': 'electrical_engineer',
}

interface BoardEntryCardV2Props {
  onSendMessage: (message: string, agentId: string) => void
}

export default function BoardEntryCardV2({
  onSendMessage,
}: BoardEntryCardV2Props) {
  const { t } = useTranslation()
  const agents = useAppStore((s) => s.agents)
  const fetchAgents = useAppStore((s) => s.fetchAgents)
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // Mirrors the freshest input value so the voice send path (deferred ~100ms by
  // ChatVoice after it sets the transcript) reads the transcript, not a stale
  // closure over `inputValue`.
  const latestInputRef = useRef('')
  const activeAgent = useAppStore((s) => s.activeAgent)
  const storeSelectedAgent = useAppStore((s) => s.selectedAgent)
  const contextId = useAppStore((s) => s.context_id)
  // The SAME store field the docked board panel writes and ChatInterface reads at
  // send time — not a second copy — so the tick survives the entry card handing
  // the first message off to the chat panel.
  const entireDataCatalog = useAppStore((s) => s.entireDataCatalog)
  const setEntireDataCatalog = useAppStore((s) => s.setEntireDataCatalog)

  // FS-575: persist the draft prompt under the same localStorage key
  // ChatInterface uses for a new conversation (`chatDraft_<ctx>_new`).
  // This card unmounts the moment the board's first files land (the
  // canvas swaps it for the chat panel) — with the shared key, the chat
  // input restores the exact prompt typed here, and the draft survives
  // reloads too. chatSlice already honors the key (stays in "new chat"
  // mode while it exists, clears it after the first send).
  const draftKey = contextId ? `chatDraft_${contextId}_new` : null

  // Restore once the context id is known. Within one context, never
  // overwrite text the user has already typed; if the context switches
  // mid-mount (board swap without unmount), the old board's text no longer
  // belongs here — replace it with the new context's draft (or clear).
  const restoredKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!draftKey) return
    const isContextSwitch = restoredKeyRef.current !== null && restoredKeyRef.current !== draftKey
    restoredKeyRef.current = draftKey
    try {
      const draft = localStorage.getItem(draftKey)
      if (draft !== null && (isContextSwitch || !latestInputRef.current)) {
        latestInputRef.current = draft
        setInputValue(draft)
      } else if (isContextSwitch) {
        latestInputRef.current = ''
        setInputValue('')
      }
    } catch {
      // Ignore localStorage errors (e.g., Safari private browsing)
    }
  }, [draftKey])

  // Save on change via setTimeout(0) — the restore effect's setInputValue
  // re-renders first and cancels the initial empty-value timer before it
  // can delete an existing draft (same contract as ChatInterface's saver).
  useEffect(() => {
    if (!draftKey) return
    const timer = setTimeout(() => {
      try {
        if (inputValue.trim()) {
          localStorage.setItem(draftKey, inputValue)
        } else {
          localStorage.removeItem(draftKey)
        }
      } catch {
        // Ignore localStorage errors
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [inputValue, draftKey])

  // @-mention autocomplete state
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false)
  const [mentionFilterQuery, setMentionFilterQuery] = useState('')
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [cursorPosition, setCursorPosition] = useState(0)

  // Only show agents that are online/available — offline agents (e.g. AI Chemical
  // Engineer toggled off) must not appear in the @-mention list, otherwise the user
  // can mention an agent that won't respond.
  const availableAgentIds = useMemo(
    () => agents.filter((a) => a.isOnline).map((a) => a.id),
    [agents],
  )

  // Ensure agents are loaded (handles direct navigation to board URL)
  useEffect(() => {
    fetchAgents()
  }, [fetchAgents])

  // Sync selected agent from store if it matches one of the enabled agents
  useEffect(() => {
    const match = agents.find((a) => a.id === activeAgent || STORE_TO_CANONICAL[a.id] === activeAgent)
    if (match) setSelectedAgentId(match.id)
  }, [activeAgent, agents])

  // Default when agents load and nothing is selected. Prefer the store's
  // selected agent — that is where the onboarding-derived default lands
  //, so a process engineer opens on the Chemical Engineer instead of
  // whichever agent sorts first. Every agent stays selectable either way.
  useEffect(() => {
    if (!selectedAgentId && agents.length > 0) {
      setSelectedAgentId(storeSelectedAgent?.id ?? agents[0].id)
    }
  }, [agents, selectedAgentId, storeSelectedAgent])

  const selectedStoreAgent = agents.find((a) => a.id === selectedAgentId) ?? agents[0]

  // Shared send path for both the typed input and voice transcripts.
  const submitMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || isSubmitting || !selectedStoreAgent) return
      setIsSubmitting(true)
      // The message rides the navigation state (initialMessage/autoSend) from
      // here — drop the draft so the chat panel doesn't also restore it into
      // its input alongside the auto-send.
      if (draftKey) {
        try {
          localStorage.removeItem(draftKey)
        } catch {
          // Ignore localStorage errors
        }
      }
      const canonicalId = STORE_TO_CANONICAL[selectedStoreAgent.id] || selectedStoreAgent.id
      onSendMessage(trimmed, canonicalId)
    },
    [isSubmitting, onSendMessage, selectedStoreAgent, draftKey],
  )

  const handleSubmit = useCallback(() => {
    submitMessage(inputValue)
  }, [submitMessage, inputValue])

  // Voice input: ChatVoice transcribes speech -> we fill the textarea -> it
  // auto-submits, mirroring the docked board chat (ChatInput). The transcript is
  // also written to latestInputRef so the deferred send reads the fresh value.
  const handleVoiceTranscript = useCallback(async (transcript: string) => {
    latestInputRef.current = transcript
    setInputValue(transcript)
  }, [])

  const handleVoiceSend = useCallback(async () => {
    submitMessage(latestInputRef.current)
  }, [submitMessage])

  // Compute the filtered agent list shown by the autocomplete — used to keep
  // keyboard nav (ArrowUp/Down, Enter) in sync with the displayed list.
  // Empty availability means no agents are available — show none (matches the
  // autocomplete's new semantics: only `undefined` falls back to all agents).
  const getFilteredAgents = useCallback((): AgentOption[] => {
    const baseAgents = availableAgentIds
      .map((id) => AGENT_REGISTRY[id])
      .filter(Boolean) as AgentOption[]
    if (!mentionFilterQuery) return baseAgents
    const q = mentionFilterQuery.toLowerCase()
    return baseAgents.filter((agent) => {
      if (
        agent.id.toLowerCase().includes(q) ||
        agent.name.toLowerCase().includes(q)
      ) {
        return true
      }
      return fuzzyMatch(mentionFilterQuery, agent.label)
    })
  }, [availableAgentIds, mentionFilterQuery])

  // Replace the @query slice with the chosen agent's mention text and sync
  // the card's selected agent so the dropdown + send target match the mention.
  const handleAgentSelect = useCallback(
    (agent: AgentOption) => {
      const before = inputValue.slice(0, cursorPosition)
      const after = inputValue.slice(cursorPosition)
      const lastAtIndex = before.lastIndexOf('@')

      if (lastAtIndex !== -1) {
        const newValue = before.slice(0, lastAtIndex) + agent.name + ' ' + after
        setInputValue(newValue)
        setTimeout(() => {
          const newCursorPos = lastAtIndex + agent.name.length + 1
          textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos)
          textareaRef.current?.focus()
        }, 0)
      }

      const storeId = Object.keys(AGENT_REGISTRY).find(
        (key) => AGENT_REGISTRY[key].id === agent.id,
      )
      if (storeId && availableAgentIds.includes(storeId)) {
        setSelectedAgentId(storeId)
      }

      setShowMentionAutocomplete(false)
      setMentionFilterQuery('')
      setMentionSelectedIndex(0)
    },
    [inputValue, cursorPosition, availableAgentIds],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    const position = e.target.selectionStart ?? value.length
    latestInputRef.current = value
    setInputValue(value)
    setCursorPosition(position)

    const textBeforeCursor = value.slice(0, position)
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1)
      // Show autocomplete while we're still inside a mention token (no space, reasonable length)
      if (!textAfterAt.includes(' ') && textAfterAt.length <= 30) {
        setShowMentionAutocomplete(true)
        setMentionFilterQuery(textAfterAt)
        setMentionSelectedIndex(0)
        return
      }
    }
    setShowMentionAutocomplete(false)
    setMentionFilterQuery('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionAutocomplete) {
      const filteredAgents = getFilteredAgents()
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionSelectedIndex((prev) =>
          prev < filteredAgents.length - 1 ? prev + 1 : 0,
        )
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredAgents.length - 1,
        )
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        if (
          filteredAgents.length > 0 &&
          mentionSelectedIndex < filteredAgents.length
        ) {
          handleAgentSelect(filteredAgents[mentionSelectedIndex])
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setShowMentionAutocomplete(false)
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const canSend = inputValue.trim().length > 0 && !isSubmitting && !!selectedStoreAgent

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-[768px] bg-white dark:bg-dark-card rounded-3xl shadow-md p-6 flex flex-col items-center gap-10">
        {/* ── Entry section: heading + avatars ── */}
        <div className="flex flex-col items-center gap-6">
          <h2 className="text-2xl font-medium text-foreground text-center">
            {t('board:entry.heading', { defaultValue: 'How can I help you today?' })}
          </h2>

          {/* Agent avatars — only enabled agents from the store */}
          <div className="flex gap-2 items-start justify-center">
            {agents.map((agent) => {
              const isSelected = agent.id === selectedAgentId
              // Disabled agents stay visible but are inert — only online/enabled
              // agents (those showing the green dot) can be selected. Same
              // isOnline gate as the dropdown + @-mention list.
              const enabled = agent.isOnline
              const canonicalId = STORE_TO_CANONICAL[agent.id] || agent.id
              const displayName = agent.name || getDisplayName(canonicalId)
              const bgColor = AGENT_BG_COLORS[agent.id] || DEFAULT_BG_COLOR
              return (
                <button
                  key={agent.id}
                  type="button"
                  data-testid={`entry-agent-${agent.id}`}
                  onClick={() => enabled && setSelectedAgentId(agent.id)}
                  disabled={!enabled}
                  aria-disabled={!enabled}
                  title={enabled ? undefined : t('common:general.coming_soon')}
                  className={clsx(
                    'flex flex-col items-center gap-3 w-[110px] group',
                    !enabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div className="relative size-16">
                    <div
                      className={clsx(
                        'relative size-16 rounded-full overflow-hidden',
                        // The SELECTED ring carried the same #0e7490 cyan the
                        // label did, so converting only the label left the card
                        // still showing the third accent UI-19 exists to remove
                        // (greptile P2 + the Claude reviewer, both correct — the
                        // PR body claimed this was already done).
                        //
                        // The unselected ring's #a3a3a3 is Tailwind's stock
                        // neutral-400 to the byte, so naming it is a literal
                        // rename with no visual change — not a semantic token
                        // like ring-border, which would shift in dark mode.
                        isSelected
                          ? 'ring-[3px] ring-brand'
                          : 'ring-1 ring-neutral-400'
                      )}
                    >
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{ backgroundColor: bgColor }}
                      />
                      <img
                        src={agent.image}
                        alt={displayName}
                        className="relative w-full h-full object-cover rounded-full"
                        draggable={false}
                      />
                    </div>
                    {/* Online badge — sibling of the clipped avatar so it isn't
                        cut off by overflow-hidden. */}
                    {agent.isOnline && (
                      <div className="absolute bottom-0 right-0 size-3 rounded-full bg-green-500 border-2 border-white" />
                    )}
                  </div>
                  <span
                    className={clsx(
                      'text-sm font-medium text-center leading-5',
                      // was text-[#0e7490] — a third accent, in the same card
                      // as the send button above. Same token, same reason.
                      isSelected ? 'text-brand' : 'text-foreground'
                    )}
                  >
                    {displayName}
                  </span>
                  {/* Visible label, not just a hover tooltip — a greyed agent
                      with no explanation reads as broken rather than upcoming. */}
                  {!enabled && (
                    <span className="text-xs font-medium text-muted-foreground -mt-2">
                      {t('common:general.coming_soon', 'Coming soon')}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* ── Chat input section ── */}
        {/* relative wrapper anchors the autocomplete (`bottom-full`) above the
            input box without being clipped by the inner overflow-hidden card */}
        <div className="w-full relative">
          <AgentMentionAutocomplete
            isVisible={showMentionAutocomplete}
            selectedIndex={mentionSelectedIndex}
            filterQuery={mentionFilterQuery}
            onSelect={handleAgentSelect}
            onClose={() => setShowMentionAutocomplete(false)}
            availableAgentIds={availableAgentIds}
          />
          <div className="w-full rounded-2xl border border-border bg-white dark:bg-dark-card shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] overflow-hidden flex flex-col">
          {/* Textarea area */}
          <div className="flex-1 p-4 min-h-[100px]">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={t('board:entry.placeholder', {
                defaultValue: 'Type @ mention AI engineer or select action',
              })}
              className="w-full h-full bg-transparent text-base text-foreground placeholder:text-muted-foreground resize-none outline-none border-none focus:ring-0"
              rows={4}
              disabled={isSubmitting}
            />
          </div>

          {/* Footer: agent selector | mic + send */}
          <div className="flex items-center justify-between px-4 pb-4">
            {/* Agent selector */}
            <Popover open={agentDropdownOpen} onOpenChange={setAgentDropdownOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 rounded-lg hover:opacity-80 transition-opacity"
                >
                  {selectedStoreAgent && (
                    <>
                      <div
                        className="size-5 rounded-full overflow-hidden flex-shrink-0"
                        style={{ backgroundColor: AGENT_BG_COLORS[selectedStoreAgent.id] || DEFAULT_BG_COLOR }}
                      >
                        <img
                          src={selectedStoreAgent.image}
                          alt={selectedStoreAgent.name}
                          className="size-5 object-cover"
                          draggable={false}
                        />
                      </div>
                      <span className="text-base text-foreground">
                        {selectedStoreAgent.name || getDisplayName(STORE_TO_CANONICAL[selectedStoreAgent.id] || selectedStoreAgent.id)}
                      </span>
                    </>
                  )}
                  <ChevronDown className="size-5 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="top"
                sideOffset={8}
                className="w-56 p-1"
              >
                {/* Scope toggle — the same control the docked board panel puts in
                    its agent menu. It was absent here, so a board with no cards
                    yet — the moment a user is most likely to want the whole
                    catalog rather than a board holding nothing — had no way to
                    ask for it. The flag is read from the store at send time by
                    ChatInterface, and this card hands its first message to that
                    same path, so a tick here reaches the request. */}
                {isFeatureAllowedInEmbed('catalogScope') && (
                  <>
                    <CatalogScopeToggle checked={entireDataCatalog} onChange={setEntireDataCatalog} />
                    <div className="my-1 border-t border-border" />
                  </>
                )}
                {agents.map((agent) => {
                  const isActive = agent.id === selectedAgentId
                  // Only online/enabled agents are selectable here too.
                  const enabled = agent.isOnline
                  const canonicalId = STORE_TO_CANONICAL[agent.id] || agent.id
                  const displayName = agent.name || getDisplayName(canonicalId)
                  const bgColor = AGENT_BG_COLORS[agent.id] || DEFAULT_BG_COLOR
                  return (
                    <button
                      key={agent.id}
                      type="button"
                      data-testid={`entry-agent-menu-${agent.id}`}
                      disabled={!enabled}
                      aria-disabled={!enabled}
                      title={enabled ? undefined : t('common:general.coming_soon')}
                      onClick={() => {
                        if (!enabled) return
                        setSelectedAgentId(agent.id)
                        setAgentDropdownOpen(false)
                      }}
                      className={clsx(
                        'w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm transition-colors',
                        !enabled
                          ? 'opacity-50 cursor-not-allowed text-muted-foreground'
                          : isActive
                            ? 'bg-accent text-foreground font-medium'
                            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                      )}
                    >
                      <div
                        className="size-5 rounded-full overflow-hidden flex-shrink-0"
                        style={{ backgroundColor: bgColor }}
                      >
                        <img src={agent.image} alt={displayName} className="size-5 object-cover" draggable={false} />
                      </div>
                      <span className="truncate">{displayName}</span>
                      {!enabled && (
                        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                          {t('common:general.coming_soon', 'Coming soon')}
                        </span>
                      )}
                    </button>
                  )
                })}
              </PopoverContent>
            </Popover>

            {/* Mic + Send */}
            <div className="flex items-center gap-4">
              <ChatVoice
                onTranscript={handleVoiceTranscript}
                handleSendMessage={handleVoiceSend}
                isRecording={isRecording}
                setIsRecording={setIsRecording}
                useAzure={true}
                borderless
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSend}
                // UI-19: this was bg-[#0369a1] with a #075985 hover — a blue,
                // while every other primary action on the board is the brand
                // teal (the toolbar "+", and the chat panel's own send button,
                // which the scoped CSS in BoardChatPanelV2 already forces to
                // #0e7490). Two accents on the same surface read as two
                // different kinds of action. Uses the `brand` token from
                // tailwind.config rather than another literal, which is what
                // that token's own comment asks for: "Use bg-brand /
                // hover:bg-brand-hover instead of repeating the bg-[#0e7490]
                // literal scattered through the app."
                className={clsx(
                  'size-9 rounded-md flex items-center justify-center transition-colors',
                  canSend
                    ? 'bg-brand hover:bg-brand-hover text-white'
                    : 'bg-brand opacity-50 text-white cursor-not-allowed'
                )}
                aria-label={t('board:entry.send', { defaultValue: 'Send message' })}
              >
                {isSubmitting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ArrowRight className="size-4" />
                )}
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  )
}
